/**
 * ValidationToolsTab
 *
 * Story 72: Validation Tools for Enrichment Review
 *
 * Features:
 * - Run validation checks on products (title vs feed/enriched, range, enum)
 * - Scope validation to category or selected products
 * - View results summary and detailed issues
 * - Bulk mark products as suspicious for review
 *
 * Date: 2026-01-23
 */

import React, { useState, useEffect } from 'react';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Search,
  Flag,
  BarChart3,
  ListChecks,
  XCircle,
  Sparkles,
  ChevronDown,
  ChevronRight,
  EyeOff,
  Undo2,
  Download,
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/use-toast';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';

// Types
interface ValidationIssue {
  productId: string;
  productName: string;
  category?: string;
  // Product identification fields
  sku?: string;
  brand?: string;
  gtin?: string;
  mpn?: string;
  availability?: string;
  imageUrl?: string;
  price?: string | number;
  productUrl?: string;
  // Issue details
  checkType: string;
  field: string;
  titleValue?: unknown;
  feedValue?: unknown;
  enrichedValue?: unknown;
  expectedRange?: string;
  allowedValues?: string[];
  severity: 'critical' | 'warning';
  message: string;
}

interface FilteredIssue extends ValidationIssue {
  filterSource: 'rule-based' | 'ai-validation';
  filterReason: string;
}

interface LlmValidationStats {
  originalCount: number;
  validatedCount: number;
  filteredOut: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
}

interface ValidationSummary {
  totalChecked: number;
  issuesFound: number;
  byType: Record<string, number>;
  llmValidation?: LlmValidationStats;
}

interface ValidationResults {
  summary: ValidationSummary;
  issues: ValidationIssue[];
  filteredIssues?: FilteredIssue[];
}

interface Category {
  path: string;
  name: string;
  count: number;
}

const CHECK_TYPES = [
  { id: 'title_vs_feed', label: 'Title vs Feed Attributes', description: 'Compare specs in title to feed data' },
  { id: 'title_vs_enriched', label: 'Title vs Enriched Attributes', description: 'Compare specs in title to AI-enriched values' },
  { id: 'range_validation', label: 'Out-of-Range Values', description: 'Check values against min/max ranges' },
  { id: 'missing_critical', label: 'Missing Critical Specs', description: 'Verify required specs are present' },
  { id: 'enum_validation', label: 'Enum Mismatch', description: 'Check values match allowed enum values' },
];

const CHECK_TYPE_LABELS: Record<string, string> = {
  title_vs_feed: 'Title/Feed Mismatch',
  title_vs_enriched: 'Title/Enriched Mismatch',
  range_validation: 'Out-of-Range',
  missing_critical: 'Missing Critical',
  enum_validation: 'Enum Mismatch',
};

export function ValidationToolsTab() {
  const { selectedSite } = useSite();
  const { toast } = useToast();

  // Form state
  const [scope, setScope] = useState<'category' | 'products'>('category');
  const [categoryId, setCategoryId] = useState<string>('');
  const [selectedChecks, setSelectedChecks] = useState<string[]>(['title_vs_enriched', 'range_validation']);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [useLlmValidation, setUseLlmValidation] = useState(true); // Enable AI validation by default

  // Results state
  const [isRunning, setIsRunning] = useState(false);
  const [progressMessage, setProgressMessage] = useState<string>('');
  const [results, setResults] = useState<ValidationResults | null>(null);
  const [filterCheckType, setFilterCheckType] = useState<string | null>(null);
  const [selectedIssues, setSelectedIssues] = useState<Set<string>>(new Set());
  const [isMarking, setIsMarking] = useState(false);
  const [showFilteredIssues, setShowFilteredIssues] = useState(false);

  // Load categories on mount
  useEffect(() => {
    if (selectedSite?.id) {
      loadCategories();
    }
  }, [selectedSite?.id]);

  const loadCategories = async () => {
    if (!selectedSite?.id) return;
    setIsLoadingCategories(true);
    try {
      const response = await authFetch(`${API_URL}/product-enrichment/validation-tools/categories?siteId=${selectedSite.id}`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (err) {
      console.error('Failed to load categories:', err);
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const handleCheckToggle = (checkId: string, checked: boolean) => {
    if (checked) {
      setSelectedChecks([...selectedChecks, checkId]);
    } else {
      setSelectedChecks(selectedChecks.filter(c => c !== checkId));
    }
  };

  const handleRunValidation = async () => {
    if (!selectedSite?.id) {
      toast({ title: 'Error', description: 'No site selected', variant: 'destructive' });
      return;
    }

    if (selectedChecks.length === 0) {
      toast({ title: 'Error', description: 'Select at least one check type', variant: 'destructive' });
      return;
    }

    if (scope === 'category' && !categoryId) {
      toast({ title: 'Error', description: 'Select a category', variant: 'destructive' });
      return;
    }

    setIsRunning(true);
    setProgressMessage('Starting validation...');
    setResults(null);
    setFilterCheckType(null);
    setSelectedIssues(new Set());
    setShowFilteredIssues(false);

    try {
      const response = await authFetch(`${API_URL}/product-enrichment/validation-tools/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId: selectedSite.id,
          scope,
          categoryId: scope === 'category' ? categoryId : undefined,
          checks: selectedChecks,
          useLlmValidation,
          async: true, // Enable async mode to avoid Cloudflare timeout
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Validation failed');
      }

      const data = await response.json();

      // Handle async mode - poll for job completion
      if (data.async && data.jobId) {
        setProgressMessage('Running validation checks...');

        // Poll for job completion
        const pollInterval = setInterval(async () => {
          try {
            const jobResponse = await authFetch(`${API_URL}/jobs/${data.jobId}`);
            if (!jobResponse.ok) {
              clearInterval(pollInterval);
              throw new Error('Failed to check job status');
            }

            const jobData = await jobResponse.json();

            if (jobData.progress?.message) {
              setProgressMessage(jobData.progress.message);
            }

            if (jobData.status === 'completed' && jobData.result) {
              clearInterval(pollInterval);
              setResults({
                summary: jobData.result.summary,
                issues: jobData.result.issues,
                filteredIssues: jobData.result.filteredIssues,
              });
              setIsRunning(false);
              setProgressMessage('');
              toast({
                title: 'Validation Complete',
                description: `Found ${jobData.result.summary?.issuesFound || 0} issues in ${jobData.result.summary?.totalChecked || 0} products`,
              });
            } else if (jobData.status === 'failed') {
              clearInterval(pollInterval);
              setIsRunning(false);
              setProgressMessage('');
              toast({
                title: 'Validation Failed',
                description: jobData.error || 'Unknown error',
                variant: 'destructive',
              });
            }
          } catch (pollErr) {
            clearInterval(pollInterval);
            setIsRunning(false);
            setProgressMessage('');
            toast({
              title: 'Validation Failed',
              description: pollErr instanceof Error ? pollErr.message : 'Unknown error',
              variant: 'destructive',
            });
          }
        }, 3000); // Poll every 3 seconds

        // Timeout after 5 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          if (isRunning) {
            setIsRunning(false);
            setProgressMessage('');
            toast({
              title: 'Validation Timeout',
              description: 'Validation is taking too long. Please try with a smaller category.',
              variant: 'destructive',
            });
          }
        }, 300000);

        return; // Exit early, polling will handle the rest
      }

      // Handle sync mode (fallback)
      setResults({
        summary: data.summary,
        issues: data.issues,
        filteredIssues: data.filteredIssues,
      });

      toast({
        title: 'Validation Complete',
        description: `Found ${data.summary.issuesFound} issues in ${data.summary.totalChecked} products`,
      });
      setIsRunning(false);
      setProgressMessage('');
    } catch (err) {
      toast({
        title: 'Validation Failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
      setIsRunning(false);
      setProgressMessage('');
    }
  };

  const handleMarkSuspicious = async (mode: 'selected' | 'all') => {
    if (!results) return;
    if (!selectedSite?.id) {
      toast({ title: 'No site selected', variant: 'destructive' });
      return;
    }

    const issuesToMark = mode === 'all'
      ? results.issues
      : results.issues.filter(i => selectedIssues.has(i.productId));

    if (issuesToMark.length === 0) {
      toast({ title: 'No products selected', variant: 'destructive' });
      return;
    }

    // Group issues by product to preserve field-level details
    const issuesByProduct: Record<string, typeof issuesToMark> = {};
    for (const issue of issuesToMark) {
      if (!issuesByProduct[issue.productId]) {
        issuesByProduct[issue.productId] = [];
      }
      issuesByProduct[issue.productId].push(issue);
    }

    setIsMarking(true);
    try {
      const response = await authFetch(`${API_URL}/product-enrichment/validation-tools/mark-suspicious`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId: selectedSite?.id,
          issuesByProduct,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to mark products');
      }

      const data = await response.json();
      toast({
        title: 'Products Marked',
        description: `${data.marked} products marked as suspicious${data.alreadySuspicious > 0 ? ` (${data.alreadySuspicious} were already marked)` : ''}`,
      });

      setSelectedIssues(new Set());
    } catch (err) {
      toast({
        title: 'Failed to Mark Products',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsMarking(false);
    }
  };

  const handleIssueSelect = (productId: string, checked: boolean) => {
    const newSelected = new Set(selectedIssues);
    if (checked) {
      newSelected.add(productId);
    } else {
      newSelected.delete(productId);
    }
    setSelectedIssues(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const filteredIssues = filterCheckType
        ? results?.issues.filter(i => i.checkType === filterCheckType)
        : results?.issues;
      setSelectedIssues(new Set(filteredIssues?.map(i => i.productId) || []));
    } else {
      setSelectedIssues(new Set());
    }
  };

  // Move a filtered issue back to the main issues list (user override)
  const handleUnfilterIssue = (issueIndex: number) => {
    if (!results || !results.filteredIssues) return;

    const issueToMove = results.filteredIssues[issueIndex];
    if (!issueToMove) return;

    // Create a clean issue object without the filter-specific properties
    const cleanIssue: ValidationIssue = {
      productId: issueToMove.productId,
      productName: issueToMove.productName,
      checkType: issueToMove.checkType,
      field: issueToMove.field,
      titleValue: issueToMove.titleValue,
      feedValue: issueToMove.feedValue,
      enrichedValue: issueToMove.enrichedValue,
      expectedRange: issueToMove.expectedRange,
      allowedValues: issueToMove.allowedValues,
      severity: issueToMove.severity,
      message: issueToMove.message,
    };

    // Update results: remove from filteredIssues, add to issues
    const newFilteredIssues = results.filteredIssues.filter((_, idx) => idx !== issueIndex);
    const newIssues = [...results.issues, cleanIssue];

    // Update summary counts
    const newByType = { ...results.summary.byType };
    newByType[cleanIssue.checkType] = (newByType[cleanIssue.checkType] || 0) + 1;

    setResults({
      ...results,
      summary: {
        ...results.summary,
        issuesFound: results.summary.issuesFound + 1,
        byType: newByType,
        ...(results.summary.llmValidation && {
          llmValidation: {
            ...results.summary.llmValidation,
            filteredOut: results.summary.llmValidation.filteredOut - 1,
          },
        }),
      },
      issues: newIssues,
      filteredIssues: newFilteredIssues.length > 0 ? newFilteredIssues : undefined,
    });

    toast({
      title: 'Issue Restored',
      description: `"${cleanIssue.field}" issue moved to the main issues list`,
    });
  };

  const filteredIssues = filterCheckType
    ? results?.issues.filter(i => i.checkType === filterCheckType)
    : results?.issues;

  const uniqueProductCount = new Set(filteredIssues?.map(i => i.productId) || []).size;

  // Export issues to CSV
  const handleExportCsv = () => {
    if (!results || !filteredIssues || filteredIssues.length === 0) return;

    // Helper to escape CSV values
    const escapeCSV = (value: unknown): string => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const headers = [
      'Product ID',
      'SKU',
      'Product Name',
      'Brand',
      'Product URL',
      'Category',
      'Price',
      'GTIN',
      'MPN',
      'Availability',
      'Image URL',
      'Check Type',
      'Field',
      'Severity',
      'Message'
    ];

    const rows = filteredIssues.map(issue => [
      escapeCSV(issue.productId),
      escapeCSV(issue.sku || issue.productId),
      escapeCSV(issue.productName),
      escapeCSV(issue.brand),
      escapeCSV(issue.productUrl),
      escapeCSV(issue.category || categoryId),
      escapeCSV(issue.price),
      escapeCSV(issue.gtin),
      escapeCSV(issue.mpn),
      escapeCSV(issue.availability),
      escapeCSV(issue.imageUrl),
      escapeCSV(CHECK_TYPE_LABELS[issue.checkType] || issue.checkType),
      escapeCSV(issue.field),
      escapeCSV(issue.severity),
      escapeCSV(issue.message),
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `validation-issues-${filterCheckType || 'all'}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: 'Exported',
      description: `${filteredIssues.length} issues exported to CSV`,
    });
  };

  return (
    <div className="space-y-6">
      {/* Configuration Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ListChecks className="h-5 w-5" />
            Run Validation Checks
          </CardTitle>
          <CardDescription>
            Select checks to run on products. Issues found can be marked for review in the Review Suspicious tab.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Scope Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Scope</label>
            <Select value={scope} onValueChange={(v) => setScope(v as 'category' | 'products')}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select scope" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="category">Category</SelectItem>
                <SelectItem value="products" disabled>Selected Products (coming soon)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Category Selection */}
          {scope === 'category' && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Category</label>
              <Select value={categoryId} onValueChange={setCategoryId} disabled={isLoadingCategories}>
                <SelectTrigger className="w-[400px]">
                  <SelectValue placeholder={isLoadingCategories ? "Loading categories..." : "Select category"} />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.path} value={cat.path}>
                      {cat.name} ({cat.count} products)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Check Types */}
          <div className="space-y-3">
            <label className="text-sm font-medium">Validation Checks</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {CHECK_TYPES.map((check) => (
                <div
                  key={check.id}
                  className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-muted/50"
                >
                  <Checkbox
                    id={check.id}
                    checked={selectedChecks.includes(check.id)}
                    onCheckedChange={(checked) => handleCheckToggle(check.id, checked === true)}
                  />
                  <div className="space-y-1">
                    <label htmlFor={check.id} className="text-sm font-medium cursor-pointer">
                      {check.label}
                    </label>
                    <p className="text-xs text-muted-foreground">{check.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* AI Validation Toggle */}
          <div className="flex items-center justify-between p-4 border rounded-lg bg-gradient-to-r from-purple-500/10 to-blue-500/10">
            <div className="flex items-center gap-3">
              <Sparkles className="h-5 w-5 text-purple-500" />
              <div>
                <div className="font-medium">AI-Powered Validation</div>
                <p className="text-xs text-muted-foreground">
                  Use GPT-4o-mini to filter false positives in title comparisons
                </p>
              </div>
            </div>
            <Switch
              checked={useLlmValidation}
              onCheckedChange={setUseLlmValidation}
            />
          </div>

          {/* Run Button */}
          <Button
            onClick={handleRunValidation}
            disabled={isRunning || selectedChecks.length === 0 || (scope === 'category' && !categoryId)}
            className="w-full md:w-auto"
          >
            {isRunning ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {progressMessage || 'Running Checks...'}
              </>
            ) : (
              <>
                <Search className="h-4 w-4 mr-2" />
                Run Selected Checks
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      {results && (
        <>
          {/* Summary Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Results Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">{results.summary.totalChecked}</div>
                  <div className="text-sm text-muted-foreground">Products Checked</div>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold text-destructive">{results.summary.issuesFound}</div>
                  <div className="text-sm text-muted-foreground">Total Issues</div>
                </div>
                {results.summary.llmValidation && results.filteredIssues && results.filteredIssues.length > 0 && (
                  <div
                    className="text-center p-4 bg-muted rounded-lg cursor-pointer hover:bg-muted/70 transition-colors"
                    onClick={() => setShowFilteredIssues(!showFilteredIssues)}
                    title="Click to view filtered issues"
                  >
                    <div className="text-2xl font-bold text-green-500">{results.filteredIssues.length}</div>
                    <div className="text-sm text-muted-foreground">False Positives Removed</div>
                  </div>
                )}
              </div>

              {/* LLM Validation Banner */}
              {results.summary.llmValidation && (
                <Alert className="mb-4 border-purple-500/50 bg-purple-500/10">
                  <Sparkles className="h-4 w-4 text-purple-500" />
                  <AlertDescription>
                    <span className="font-medium">Validation Pipeline:</span>{' '}
                    {results.summary.llmValidation.preFiltered || 0} filtered by rules,{' '}
                    {results.summary.llmValidation.filteredOut} filtered by AI,{' '}
                    {results.summary.issuesFound} confirmed issues.
                  </AlertDescription>
                </Alert>
              )}

              {/* Filtered False Positives (Collapsible) */}
              {results.filteredIssues && results.filteredIssues.length > 0 && (
                <div className="border rounded-lg mb-4">
                  <button
                    onClick={() => setShowFilteredIssues(!showFilteredIssues)}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">
                        Filtered as False Positives ({results.filteredIssues.length})
                      </span>
                      <Badge variant="secondary" className="ml-2">
                        {results.filteredIssues.filter(i => i.filterSource === 'rule-based').length} rule-based,{' '}
                        {results.filteredIssues.filter(i => i.filterSource === 'ai-validation').length} AI-filtered
                      </Badge>
                    </div>
                    {showFilteredIssues ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </button>
                  {showFilteredIssues && (
                    <div className="border-t p-4">
                      <ScrollArea className="h-[300px]">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-[50px]">Action</TableHead>
                              <TableHead>Product</TableHead>
                              <TableHead>Field</TableHead>
                              <TableHead>Issue</TableHead>
                              <TableHead>Filter Source</TableHead>
                              <TableHead>Reason</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {results.filteredIssues.map((issue, idx) => (
                              <TableRow key={`filtered-${issue.productId}-${issue.field}-${idx}`} className="opacity-60 hover:opacity-100">
                                <TableCell>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleUnfilterIssue(idx)}
                                    title="Not a false positive - move to issues list"
                                    className="h-8 w-8 p-0"
                                  >
                                    <Undo2 className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                                <TableCell className="font-medium max-w-[150px] truncate" title={issue.productName}>
                                  {issue.productName}
                                </TableCell>
                                <TableCell className="font-mono text-sm">{issue.field}</TableCell>
                                <TableCell className="max-w-[200px]">
                                  <span className="text-sm">{issue.message}</span>
                                </TableCell>
                                <TableCell>
                                  <Badge variant={issue.filterSource === 'ai-validation' ? 'secondary' : 'outline'}>
                                    {issue.filterSource === 'ai-validation' ? 'AI' : 'Rules'}
                                  </Badge>
                                </TableCell>
                                <TableCell className="max-w-[200px] text-sm text-muted-foreground">
                                  {issue.filterReason}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </ScrollArea>
                    </div>
                  )}
                </div>
              )}

              {/* Breakdown by check type */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Issues by Check Type (click to filter)</label>
                <div className="flex flex-wrap gap-2">
                  <Badge
                    variant={filterCheckType === null ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => setFilterCheckType(null)}
                  >
                    All ({results.summary.issuesFound})
                  </Badge>
                  {Object.entries(results.summary.byType).map(([type, count]) => (
                    <Badge
                      key={type}
                      variant={filterCheckType === type ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => setFilterCheckType(type)}
                    >
                      {CHECK_TYPE_LABELS[type] || type} ({count})
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Issues Table */}
          {results.issues.length > 0 ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                    Issues Found
                    {filterCheckType && (
                      <Badge variant="secondary" className="ml-2">
                        Filtered: {CHECK_TYPE_LABELS[filterCheckType]}
                      </Badge>
                    )}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleExportCsv}
                      disabled={!filteredIssues || filteredIssues.length === 0}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Export CSV
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleMarkSuspicious('selected')}
                      disabled={selectedIssues.size === 0 || isMarking}
                    >
                      {isMarking ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Flag className="h-4 w-4 mr-1" />}
                      Mark Selected ({selectedIssues.size})
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleMarkSuspicious('all')}
                      disabled={isMarking}
                    >
                      {isMarking ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Flag className="h-4 w-4 mr-1" />}
                      Mark All ({uniqueProductCount})
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[50px]">
                          <Checkbox
                            checked={selectedIssues.size === uniqueProductCount && uniqueProductCount > 0}
                            onCheckedChange={handleSelectAll}
                          />
                        </TableHead>
                        <TableHead>Product</TableHead>
                        <TableHead>Check Type</TableHead>
                        <TableHead>Field</TableHead>
                        <TableHead>Issue</TableHead>
                        <TableHead>Severity</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredIssues?.map((issue, idx) => (
                        <TableRow key={`${issue.productId}-${issue.field}-${idx}`}>
                          <TableCell>
                            <Checkbox
                              checked={selectedIssues.has(issue.productId)}
                              onCheckedChange={(checked) => handleIssueSelect(issue.productId, checked === true)}
                            />
                          </TableCell>
                          <TableCell className="font-medium max-w-[200px] truncate" title={issue.productName}>
                            {issue.productName}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{CHECK_TYPE_LABELS[issue.checkType] || issue.checkType}</Badge>
                          </TableCell>
                          <TableCell className="font-mono text-sm">{issue.field}</TableCell>
                          <TableCell className="max-w-[300px]">
                            <span className="text-sm">{issue.message}</span>
                          </TableCell>
                          <TableCell>
                            {issue.severity === 'critical' ? (
                              <Badge variant="destructive">Critical</Badge>
                            ) : (
                              <Badge variant="secondary">Warning</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          ) : (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                No issues found. All checked products passed validation.
              </AlertDescription>
            </Alert>
          )}
        </>
      )}

      {/* No results yet */}
      {!results && !isRunning && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Select validation checks and a scope, then click "Run Selected Checks" to start validation.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
